import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";

// mui components
import { Box, Stack, Tab, Tabs } from "@mui/material";

// mui icons
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

// redux slice
import { selectUserDetails } from "../../features/auth/authSlice";

// custom components
import CustomLayout from "../../components/layout";
import Heading6 from "../../components/typography/heading-06";
import Heading5 from "../../components/typography/heading-05";
import PrimaryButton from "../../components/buttons/primary-button";
import CustomTable from "../../components/custom-table";
import { TabPanel } from "../../components/tab-panel";
import TemporaryDrawer from "../../components/temporary-drawer";
import SecondarySelect from "../../components/custom-select/secondary-select";

// api slice
import {
	useGetMessagesMutation,
	useGetRoleSpecificMessagesMutation,
} from "../../features/messages/messagesApiSlice";

// custom modal
import ProcessingModal from "../../components/modal/processing-modal";
import SuccessModal from "../../components/modal/success-modal";
import CommanErrorModal from "../../components/modal/common-error-modal";
import { useErrorMessages } from "../../utils/error-messages.constants";

// helper utils
import {
	MESSAGE_DRAWER_TYPES,
	MESSAGE_TYPES,
} from "../../utils/messages.constants";

// custom styles
import styles from "./index.module.scss";

const Messages = () => {
	const { getErrorMessage } = useErrorMessages();
	const userDetails = useSelector(selectUserDetails);
	const isAdmin = userDetails?.user_roles?.includes("admin") === true;

	const [tabValue, setTabValue] = useState(0);
	const [messages, setMessages] = useState([]);
	const [filteredMessages, setFilteredMessages] = useState([]);

	// Status filter for admin (Active/Expired) and non-admin (Unread/Read)
	const [statusFilter, setStatusFilter] = useState(
		isAdmin ? "Active" : "Unread"
	);

	const [getMessages, { isLoading: isGettingMessages }] =
		useGetMessagesMutation();
	const [
		getRoleSpecificMessages,
		{ isLoading: isGettingRoleSpecificMessages },
	] = useGetRoleSpecificMessagesMutation();

	// Drawer state
	const [drawerType, setDrawerType] = useState("");
	const [drawerOpen, setDrawerOpen] = useState({});
	const [drawerData, setDrawerData] = useState(null);

	// Modal states
	const [processingText, setProcessingText] = useState("");
	const [processingModalVisible, setProcessingModalVisible] = useState(false);
	const [successText, setSuccessText] = useState("");
	const [successModalVisible, setSuccessModalVisible] = useState(false);
	const [commonErrorData, setCommonErrorData] = useState(null);
	const [commonErrorModalVisible, setCommonErrorModalVisible] = useState(false);

	const handleOpenDrawer = () => {
		setDrawerOpen({
			right: true,
		});
	};

	const handleCloseDrawer = () => {
		setDrawerOpen({
			right: false,
		});
		setDrawerType("");
		setDrawerData(null);
	};

	const handleOpenProcessingModal = () => {
		setProcessingModalVisible(true);
	};

	const handleCloseProcessingModal = () => {
		setProcessingModalVisible(false);
	};

	const handleOpenCommonErrorModal = useCallback((errorKey) => {
		setCommonErrorData(getErrorMessage(errorKey));
		setCommonErrorModalVisible(true);
	}, [getErrorMessage]);

	const handleCloseCommonErrorModal = () => {
		setCommonErrorModalVisible(false);
		setCommonErrorData(null);
	};

	const fetchMessages = useCallback(async () => {
		try {
			let messagesResponse;
			if (isAdmin) {
				// Admin: Get all messages
				messagesResponse = await getMessages({
					messageType: "",
				}).unwrap();
			} else {
				// Non-admin: Get role-specific messages only
				messagesResponse = await getRoleSpecificMessages().unwrap();
			}
			console.log("Messages:fetchMessages:messagesResponse", messagesResponse);
			setMessages(messagesResponse || []);
		} catch (err) {
			console.error(
				"Messages:fetchMessages:messagesResponse: Error fetching messages:",
				err
			);
			handleOpenCommonErrorModal("ADMIN_MESSAGES_UNAVAILABLE");
		}
	}, [
		getMessages,
		getRoleSpecificMessages,
		isAdmin,
		handleOpenCommonErrorModal,
	]);

	const handleOpenSuccessModal = () => {
		setSuccessModalVisible(true);
	};

	const handleCloseSuccessModal = useCallback(() => {
		setSuccessModalVisible(false);
		fetchMessages(); // Refresh messages after success
	}, [fetchMessages]);


	const toggleDrawer = useCallback((event, drawerType, data = null) => {
		if (drawerType !== undefined && drawerType !== "") {
			setDrawerType(drawerType);
			setDrawerData(data);
			handleOpenDrawer();
		} else {
			setDrawerType("");
			setDrawerData(null);
			handleCloseDrawer();
		}
	}, []);

	function a11yProps(index) {
		return {
			id: `simple-tab-${index}`,
			"aria-controls": `simple-tabpanel-${index}`,
		};
	}

	const handleTabChange = (event, newValue) => {
		setTabValue(newValue);
		filterMessagesByCategory(newValue);
	};

	const handleStatusFilterChange = (event) => {
		setStatusFilter(event.target.value);
	};

	// Get current date for Active/Expired filtering
	const getCurrentDate = () => {
		return new Date().toISOString();
	};

	const filterMessagesByCategory = useCallback(
		(tabIndex) => {
			let categoryFilteredMessages = [];

			if (tabIndex === 0) {
				// System messages
				categoryFilteredMessages = messages.filter(
					(msg) => msg.message_type === MESSAGE_TYPES.SYSTEM
				);
			} else if (tabIndex === 1) {
				// Role Specific messages
				categoryFilteredMessages = messages.filter(
					(msg) => msg.message_type === MESSAGE_TYPES.ROLE_SPECIFIC
				);
			} else if (tabIndex === 2) {
				// General messages
				categoryFilteredMessages = messages.filter(
					(msg) => msg.message_type === MESSAGE_TYPES.GENERAL
				);
			}

			let statusFilteredMessages = categoryFilteredMessages;
			if (isAdmin) {
				// Admin: Filter by Active/Expired based on end_date
				const currentDate = getCurrentDate();
				if (statusFilter === "Active") {
					statusFilteredMessages = categoryFilteredMessages.filter(
						(msg) => msg.end_date && msg.end_date > currentDate
					);
				} else if (statusFilter === "Expired") {
					statusFilteredMessages = categoryFilteredMessages.filter(
						(msg) => msg.end_date && msg.end_date <= currentDate
					);
				}
			} else {
				// Non-admin: Show all messages (filtering is happening in custom-table component)
				statusFilteredMessages = categoryFilteredMessages;
			}

			setFilteredMessages(statusFilteredMessages);
		},
		[messages, isAdmin, statusFilter]
	);

	useEffect(() => {
		fetchMessages();
	}, [fetchMessages]);

	useEffect(() => {
		filterMessagesByCategory(tabValue);
	}, [filterMessagesByCategory, tabValue]);

	// Update status filter when admin status changes
	useEffect(() => {
		setStatusFilter(isAdmin ? "Active" : "Unread");
	}, [isAdmin]);

	const handleMessageRowClick = useCallback(
		(messageId) => {
			if (isAdmin) {
				const messageToEdit = filteredMessages.find(
					(msg) => msg.id === messageId
				);
				if (messageToEdit) {
					console.log(
						"245 test",
						MESSAGE_DRAWER_TYPES.EDIT_MESSAGE,
						messageToEdit
					);
					toggleDrawer(null, MESSAGE_DRAWER_TYPES.EDIT_MESSAGE, messageToEdit);
				}
			}
		},
		[isAdmin, filteredMessages, toggleDrawer]
	);

	const messageColumns = [
		{ id: "subject", label: "Subject", minWidth: 200 },
		{ id: "details", label: "Details", minWidth: 300 },
		{ id: "start-datetime", label: "Start DateTime (UTC)", minWidth: 250 },
		{ id: "end-datetime", label: "End DateTime (UTC)", minWidth: 250 },
	];

	if (isAdmin) {
		messageColumns.splice(2, 0, {
			id: "date-created",
			label: "Date Created",
			minWidth: 200,
		});
	}

	const breadcrumbData = [
		{
			label: "Home",
			path: "/home",
		},
		{
			label: "Messages",
			path: "/messages",
		},
	];

	return (
		<Box>
			<CustomLayout breadcrumb={breadcrumbData} disabledOverflow={true}>
				<Stack direction="column" className={styles.stackContainer}>
					{/* Header */}
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						className={styles.headerContainer}
					>
						<Heading5 text="Messages Dashboard" fontType="semibold" />
						{isAdmin && (
							<PrimaryButton
								text="Add New Message"
								startIcon={<AddOutlinedIcon className={styles.addIcon} />}
								onClick={(e) =>
									toggleDrawer(e, MESSAGE_DRAWER_TYPES.ADD_MESSAGE)
								}
							/>
						)}
					</Stack>

					{/* Tabs */}
					<Stack direction="column" className={styles.tabsMainContainer}>
						<Tabs
							value={tabValue}
							onChange={handleTabChange}
							aria-label="message tabs"
							TabIndicatorProps={{
								style: {
									backgroundColor: "#49787F",
								},
							}}
							className={styles.tabsContainer}
						>
							<Tab
								className={
									tabValue === 0
										? `${styles.tabBtnContainer} ${styles.activeTabBtnContainer}`
										: styles.tabBtnContainer
								}
								label={
									<Stack direction="row" className={styles.tabLabelContainer}>
										<Heading6
											text="System"
											fontType="semibold"
											extraClass={
												tabValue === 0 ? styles.activeTabText : styles.tabText
											}
										/>
									</Stack>
								}
								{...a11yProps(0)}
							/>
							<Tab
								className={
									tabValue === 1
										? `${styles.tabBtnContainer} ${styles.activeTabBtnContainer}`
										: styles.tabBtnContainer
								}
								label={
									<Stack direction="row" className={styles.tabLabelContainer}>
										<Heading6
											text="Role Specific"
											fontType="semibold"
											extraClass={
												tabValue === 1 ? styles.activeTabText : styles.tabText
											}
										/>
									</Stack>
								}
								{...a11yProps(1)}
							/>
							<Tab
								className={
									tabValue === 2
										? `${styles.tabBtnContainer} ${styles.activeTabBtnContainer}`
										: styles.tabBtnContainer
								}
								label={
									<Stack direction="row" className={styles.tabLabelContainer}>
										<Heading6
											text="General"
											fontType="semibold"
											extraClass={
												tabValue === 2 ? styles.activeTabText : styles.tabText
											}
										/>
									</Stack>
								}
								{...a11yProps(2)}
							/>
						</Tabs>

						{/* Status Filter - Only show for admin users */}
						{isAdmin && (
							<Stack
								direction="row"
								alignItems="center"
								gap={2}
								className={styles.statusFilterContainer}
							>
								<Heading6 text="Status" fontType="semibold" />
								<SecondarySelect
									id="status-filter"
									value={statusFilter}
									handleChange={handleStatusFilterChange}
									options={[
										{ value: "Active", label: "Active Messages" },
										{ value: "Expired", label: "Expired Messages" },
									]}
									extraClass={styles.statusFilterSelect}
								/>
							</Stack>
						)}

						{/* Tab Panels */}
						<TabPanel value={tabValue} index={0}>
							<CustomTable
								enableSort={true}
								columnData={messageColumns}
								rowData={filteredMessages}
								tableType="messages"
								setSortedData={setFilteredMessages}
								isLoading={isGettingMessages || isGettingRoleSpecificMessages}
								onMessageRowClick={handleMessageRowClick}
								isAdmin={isAdmin}
							/>
						</TabPanel>

						<TabPanel value={tabValue} index={1}>
							<CustomTable
								enableSort={false}
								columnData={messageColumns}
								rowData={filteredMessages}
								tableType="messages"
								setSortedData={setFilteredMessages}
								isLoading={isGettingMessages || isGettingRoleSpecificMessages}
								onMessageRowClick={handleMessageRowClick}
								isAdmin={isAdmin}
							/>
						</TabPanel>

						<TabPanel value={tabValue} index={2}>
							<CustomTable
								enableSort={false}
								columnData={messageColumns}
								rowData={filteredMessages}
								tableType="messages"
								setSortedData={setFilteredMessages}
								isLoading={isGettingMessages || isGettingRoleSpecificMessages}
								onMessageRowClick={handleMessageRowClick}
								isAdmin={isAdmin}
							/>
						</TabPanel>
					</Stack>
				</Stack>

				{/* Drawer */}
				<TemporaryDrawer
					drawerOpen={drawerOpen}
					drawerDirection="right"
					drawerType={drawerType}
					toggleDrawer={toggleDrawer}
					handleCloseDrawer={handleCloseDrawer}
					handleOpenProcessingModal={handleOpenProcessingModal}
					handleCloseProcessingModal={handleCloseProcessingModal}
					setProcessingText={setProcessingText}
					handleOpenSuccessModal={handleOpenSuccessModal}
					setSuccessText={setSuccessText}
					handleOpenCommonErrorModal={handleOpenCommonErrorModal}
					drawerData={drawerData}
					refreshMessages={fetchMessages}
				/>

				{/* Modals */}
				<ProcessingModal open={processingModalVisible} text={processingText} />
				<SuccessModal
					open={successModalVisible}
					handleClose={handleCloseSuccessModal}
					text={successText}
				/>
				<CommanErrorModal
					isOpen={commonErrorModalVisible}
					onClose={handleCloseCommonErrorModal}
					title={commonErrorData?.title}
					message={commonErrorData?.message}
					errorKey={commonErrorData?.error_key}
					errorCode={commonErrorData?.code}
					errorResponse={commonErrorData?.error_response}
				/>
			</CustomLayout>
		</Box>
	);
};

export default Messages;
